import React, { useEffect, useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ModalCheckDomain from "./ModalCheckDomain";
import { formatDate } from "utils/isoDate";
import DomainVerificationIcon from "assets/icons/DomainVerificationIcon";
import Union from "assets/icons/Union";
import { Box, Typography, Tooltip, styled, TooltipProps, tooltipClasses } from "@mui/material";
import CustomTable, { IColumn } from "components/CustomTable";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import useFetch from "hooks/useFetch";
import { getToken } from "utils/getToken";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppStore } from "store/appStore";
import { IPwa } from "interfaces/pwas";
import useCautionStore from "store/cautionStore";
import CautionModal from "components/CautionModal";
import { useTranslation } from "react-i18next";
import CustomButton from "components/Button";
import { useAuthStore } from "store/authStore";
import StopIcon from "assets/icons/StopIcon";

interface IDomain {
  name: string;
  listedDomain: string;
  createdAt: string;
  nsFirst: string;
  nsSecond: string;
  status: string;
  _id: string;
  pwa?: string;
  type: string;
}

interface DomainTableProps {
  pwas: IPwa[];
  domains: IDomain[];
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
  getDomains: () => any;
  setDomains: (domainList: any) => void;
  buyTMPDomainId?: string;
}

const textTableStyle = {
  fontFamily: "var(--cygreRegular), sans-serif",
  fontSize: "14px",
  paddingLeft: "6px",
};

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#2E263D",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    fontFamily: "var(--cygreRegular), sans-serif",
    fontSize: "14px",
    backgroundColor: "#2E263D",
  },
}));

const DomainTable: React.FC<DomainTableProps> = ({ pwas, domains, getDomains, buyTMPDomainId, setDomains }) => {
  const { appData, setIsChanged } = useAppStore();
  const { setUser } = useAuthStore();
  const { id: linkId } = useParams();
  const [openCheckModal, setOpenCheckModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<IDomain | null>(null);
  const [editDomain, setEditDomain] = useState<IDomain | null>(null);
  const [attachDomainId, setAttachDomainId] = useState("");
  const [selectedFrozenDomainId, setSelectedFrozenDomainId] = useState<string>("");
  const [selectedFrozenDomainName, setSelectedFrozenDomainName] = useState<string>("");
  const [isDomainExtendLoading, setIsDomainExtendLoading] = useState<boolean>(false);
  const [hoveredRowId, setHoveredRowId] = React.useState<string | null>(null);
  const token = getToken();
  const id = linkId || appData?._id;
  const storageDomain = localStorage.getItem("tmpDomainID");
  const [tmpDomainID, setTmpDomainID] = useState<string | null>(storageDomain);
  const {
    isDomainModalOpen,
    openDomainModal,
    closeModal,
    handleDomainToDeleteChange,
    openDomainDeleteModal,
    isDomainDeleteModalOpen,
    domainToDelete,
  } = useCautionStore();
  const { t } = useTranslation();

  useEffect(() => {
    if (buyTMPDomainId) {
      const domain = domains.find((item) => item.listedDomain === buyTMPDomainId);
      if (domain) {
        setTmpDomainID(domain?._id);
        localStorage.setItem("tmpDomainID", domain?._id);
      }
    }
  }, [buyTMPDomainId, domains]);

  useEffect(() => {
    if (appData?.domain?.status) {
      setTmpDomainID(null);
      localStorage.removeItem("tmpDomainID");
      return;
    }
  }, [tmpDomainID, appData?.domain?.status]);

  useEffect(() => {
    if (attachDomainId) {
      void attachDomainAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachDomainId]);

  useEffect(() => {
    if (!selectedFrozenDomainId) {
      return;
    }

    handleDomainExtend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFrozenDomainId]);

  const { loading: loadingDomain, triggerFetch: attachDomain } = useFetch({
    url: `pwa/${id}/domain`,
    method: "POST",
    body: {
      domain: attachDomainId,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { loading: deleteDomainLoading, triggerFetch: deleteDomain } = useFetch({
    url: `domain/${domainToDelete?.id}`,
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { triggerFetch: buyDomain } = useFetch({
    url: `list-domain/${selectedFrozenDomainId}/buy`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // const { triggerFetch: buyAndAttachDomain } = useFetch({
  //   url: `list-domain/${selectedFrozenDomainId}/buy-and-attach-to-pwa/${id}`,
  //   method: "GET",
  //   headers: {
  //     Authorization: `Bearer ${token}`,
  //   },
  // });

  const { triggerFetch: setDomain } = useFetch({
    url: "tracking/keitaro-proxy",
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: {
      method: "POST",
      uri: "/domains",
      requestBody: {
        name: `link.${selectedFrozenDomainName}`,
      },
    },
  });

  const { triggerFetch: getUserInfo } = useFetch({
    url: "profile/user-info",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  async function attachDomainAction() {
    if (!appData?._id) {
      setTmpDomainID(attachDomainId);
      localStorage.setItem("tmpDomainID", attachDomainId);
      return;
    }

    try {
      const result = await attachDomain();
      if (!result.success) {
        toast.error(result?.message || "Attach domain failed");
        return;
      }
      toast.success("Domain attached successfully");
      setIsChanged(true);
      await getDomains();
    } catch (error: any) {
      toast.error("Error", error?.message || "Attach domain failed");
    }
  }

  async function changePwaDomain(domain: any) {
    setAttachDomainId(domain._id);
    closeModal();
  }

  const handleRowClick = (domain: IDomain) => {
    setSelectedDomain(domain);
    setOpenCheckModal(true);
  };

  const handleDomainInUseCheck = (domainId: string) => {
    const domainsIdInUse = pwas
      .filter((pwa) => pwa._id !== id)
      .map((pwa) => pwa.domain?.listedDomain)
      .filter((domain) => domain !== undefined);
    return domainsIdInUse.includes(domainId);
  };

  const handleChangeDomainClick = (event: React.MouseEvent<unknown>, row: IDomain) => {
    const isDomainInUse = handleDomainInUseCheck(row.listedDomain);

    if (isDomainInUse) {
      setEditDomain(row);
      openDomainModal();
    } else {
      void changePwaDomain(row);
    }
  };

  const handleDeleteDomainBtnClick = (row: IDomain) => {
    const domainToDeleteInfo = { name: row.name, id: row._id };
    handleDomainToDeleteChange(domainToDeleteInfo);
    openDomainDeleteModal();
  };

  const userInfo = async () => {
    const res = await getUserInfo();
    if (res.data) {
      localStorage.setItem("userData", JSON.stringify(res.data));
      setUser(res.data);
    }
  };

  async function handleDomainExtend() {
    try {
      setIsDomainExtendLoading(true);
      // const requestFunction = !id ? buyDomain : buyAndAttachDomain;
      const result = await buyDomain();
      await userInfo();

      if (!result?.success) {
        toast.error(t(result?.message));
        return;
      }

      const res = await setDomain();
      if (!res.success) {
        toast.error(t("unsuccessDomainExtend"));
        return;
      }
      toast.success(t("successDomainExtend"));

      void getDomains();

      // if (!id && selectedFrozenDomainId) {
      //   const currentDomain = updatedDomains?.find((domain: any) => domain.listedDomain === selectedFrozenDomainId);

      //   if (currentDomain) {
      //     localStorage.setItem("tmpDomainID", currentDomain._id);
      //   }
      // }
    } catch (error) {
      toast.error(t("unsuccessDomainExtend"));
    } finally {
      setIsDomainExtendLoading(false);
      setSelectedFrozenDomainId("");
      setSelectedFrozenDomainName("");
    }
  }

  const handleDomainDelete = async () => {
    try {
      await deleteDomain();
      setDomains((prevDomainList: IDomain[]) => {
        const updatedDomainsArr = prevDomainList.filter((domain: IDomain) => {
          return domain._id !== domainToDelete?.id;
        });
        return updatedDomainsArr;
      });
      handleDomainToDeleteChange(null);
      closeModal();
      toast.success(t("successDomainDeletion"));
    } catch {
      toast.error(t("unsuccessfulDomainDeletion"));
    }
  };

  const columns: IColumn[] = [
    {
      id: "name",
      label: "domain",
      align: "left",
      render: (row: IDomain) => {
        const isSelected = (row.pwa && row.pwa === id) || tmpDomainID === row?._id || tmpDomainID === row?.listedDomain;
        const isShowAddIcon = !isSelected && hoveredRowId === row._id;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
          >
            {isShowAddIcon && row.status !== "frozen" && (
              <BootstrapTooltip title={"Set domain to PWA"} placement="top">
                <AddCircleIcon
                  sx={{
                    position: "absolute",
                    left: 0,
                    width: "19px",
                    height: "19px",
                    color: "#F4701B",
                  }}
                  onClick={(event) => {
                    handleChangeDomainClick(event, row);
                  }}
                />
              </BootstrapTooltip>
            )}
            {isSelected && (
              <CheckCircleIcon
                sx={{
                  position: "absolute",
                  left: 0,
                  width: "19px",
                  height: "19px",
                  color: "#F4701B",
                }}
              />
            )}
            <Typography
              sx={{
                ...textTableStyle,
                paddingLeft: "26px",
              }}
            >
              {row.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "status",
      label: "status",
      align: "left",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {row.status === "pending" && <DomainVerificationIcon />}
          {row.status === "frozen" && <StopIcon />}
          {row.status === "active" && <Union />}
          <Typography component="span" sx={{ ...textTableStyle }}>
            {row.status}
          </Typography>
        </Box>
      ),
    },
    {
      id: "createdAt",
      label: "registrationdate",
      align: "left",
      format: formatDate,
    },
    {
      id: "expiredAt",
      label: "expirationDate",
      align: "left",
      format: formatDate,
    },
    {
      id: "info",
      label: "",
      align: "left",
      render: (row) => {
        return row.type === "custom" || row.type === "purchased-custom" ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
            <BootstrapTooltip title={t("showCustomDomainInfo")} placement="top">
              <InfoIcon sx={{ width: "20px", height: "20px", color: "#F4701B" }} onClick={() => handleRowClick(row)} />
            </BootstrapTooltip>
            <BootstrapTooltip title={t("delete")} placement="top">
              <DeleteIcon
                sx={{
                  width: "20px",
                  height: "20px",
                  color: "#6D6777",
                  "&:hover": {
                    color: "#F4701B",
                  },
                }}
                onClick={() => handleDeleteDomainBtnClick(row)}
              />
            </BootstrapTooltip>
          </Box>
        ) : row.type === "bought" && row.status === "frozen" ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CustomButton
              textButton={t("extend")}
              height={24}
              customBgColor="#F4701B"
              customHoverColor="#F4701B"
              customBorderRadius="8px"
              customFontSize="12px"
              customTextTransform="none"
              customLoadingSpinerSize={18}
              onClick={() => {
                setSelectedFrozenDomainId(row.listedDomain);
                setSelectedFrozenDomainName(row.name);
              }}
              loading={isDomainExtendLoading}
            />
          </Box>
        ) : null;
      },
    },
  ];

  const rows = domains.map((domain) => ({
    ...domain,
    rowStyle: domain.pwa === id ? { backgroundColor: "rgba(244, 112, 27, 0.1) !important" } : {},
  }));

  return (
    <>
      <CustomTable columns={columns} setHoveredRowId={setHoveredRowId} rows={rows} showLoader={loadingDomain} />
      {isDomainModalOpen && (
        <CautionModal domainToChange={editDomain} handleConfirm={changePwaDomain} isLoading={loadingDomain} />
      )}
      {isDomainDeleteModalOpen && <CautionModal handleConfirm={handleDomainDelete} isLoading={deleteDomainLoading} />}
      {selectedDomain && (
        <ModalCheckDomain
          id={selectedDomain._id}
          status={selectedDomain.status}
          nsFirst={selectedDomain.nsFirst}
          nsSecond={selectedDomain.nsSecond}
          open={openCheckModal}
          setOpen={setOpenCheckModal}
          type={selectedDomain.type}
        />
      )}
    </>
  );
};

export default DomainTable;
